import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import BrandHeader from "../components/BrandHeader";
import ProgressStepper, { RESPONDENT_STEPS } from "../components/ProgressStepper";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import PasswordInput from "../components/PasswordInput";
import PasswordStrength from "../components/PasswordStrength";
import GoogleSignInButton from "../components/GoogleSignInButton";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { toast } from "sonner";

export default function RespondentRegister() {
  const [form, setForm] = useState({
    email: "", password: "", name: "", dob: "", gender: "prefer_not_to_say",
    contact: "", preferred_language: "en", profession: "", profession_other: "",
    organization: "",
  });
  const [professions, setProfessions] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const { registerRespondent } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    api.get("/professions").then((r) => setProfessions(r.data)).catch(() => {});
  }, []);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (!form.profession) {
      toast.error("Please select a profession");
      return;
    }
    setSubmitting(true);
    try {
      await registerRespondent(form);
      toast.success("Registration complete");
      nav("/consent");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <BrandHeader />
      <main className="mano-container-narrow py-12">
        <ProgressStepper steps={RESPONDENT_STEPS} current="register" />
        <div className="mb-8">
          <h1 className="font-display font-semibold text-3xl sm:text-4xl tracking-tight">Create your account</h1>
          <p className="text-muted-foreground mt-3 leading-relaxed">
            Your profile stays private. We collect only what is needed to interpret your screening results.
          </p>
        </div>

        <Card className="mano-card">
          <CardContent className="p-6 md:p-8 space-y-5">
            <GoogleSignInButton redirectPath="/consent" testId="register-google-btn"
                                label="Sign up with Google" />
            <div className="relative flex items-center" data-testid="register-divider">
              <div className="flex-grow border-t border-border/60" />
              <span className="mx-3 text-[10px] uppercase tracking-wider text-muted-foreground">
                or with email
              </span>
              <div className="flex-grow border-t border-border/60" />
            </div>
            <form onSubmit={submit} className="space-y-5" data-testid="register-form">
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="name">Full name</Label>
                  <Input id="name" required value={form.name} onChange={update("name")}
                    data-testid="register-name" />
                </div>
                <div>
                  <Label htmlFor="contact">Contact number</Label>
                  <Input id="contact" required value={form.contact} onChange={update("contact")}
                    data-testid="register-contact" />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" required value={form.email} onChange={update("email")}
                    data-testid="register-email" />
                </div>
                <div>
                  <Label htmlFor="password">Password (min 8 chars)</Label>
                  <PasswordInput id="password" required minLength={8}
                    value={form.password} onChange={update("password")}
                    data-testid="register-password" />
                  <PasswordStrength value={form.password} testId="register-password-strength" />
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-5">
                <div>
                  <Label htmlFor="dob">Date of birth</Label>
                  <Input id="dob" type="date" required value={form.dob} onChange={update("dob")}
                    data-testid="register-dob" />
                </div>
                <div>
                  <Label>Gender</Label>
                  <Select value={form.gender} onValueChange={update("gender")}>
                    <SelectTrigger data-testid="register-gender-trigger"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="non_binary">Non-binary</SelectItem>
                      <SelectItem value="prefer_not_to_say">Prefer not to say</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Preferred language</Label>
                  <Select value={form.preferred_language} onValueChange={update("preferred_language")}>
                    <SelectTrigger data-testid="register-language-trigger"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="en">English</SelectItem>
                      <SelectItem value="hi">हिन्दी (coming soon)</SelectItem>
                      <SelectItem value="ta">தமிழ் (coming soon)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Profession</Label>
                <Select value={form.profession} onValueChange={update("profession")}>
                  <SelectTrigger data-testid="register-profession-trigger"><SelectValue placeholder="Select your profession" /></SelectTrigger>
                  <SelectContent>
                    {professions.map((p) => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {form.profession === "Other" && (
                <div>
                  <Label htmlFor="profession_other">Please specify</Label>
                  <Input id="profession_other" value={form.profession_other} onChange={update("profession_other")}
                    data-testid="register-profession-other" />
                </div>
              )}

              <div>
                <Label htmlFor="organization">Organization (school, workplace or clinic)</Label>
                <Input id="organization" value={form.organization} onChange={update("organization")}
                  placeholder="e.g. Northgate Wellness Center"
                  data-testid="register-organization" />
                <p className="text-xs text-muted-foreground mt-1">
                  Optional. Lets us aggregate anonymised results for your organisation without ever sharing individual scores.
                </p>
              </div>

              <Button type="submit" size="lg" disabled={submitting}
                className="rounded-full w-full sm:w-auto px-10 mano-lift" data-testid="register-submit">
                {submitting ? "Creating..." : "Continue to consent"}
              </Button>

              <div className="text-sm text-muted-foreground pt-2">
                Already registered?{" "}
                <Link to="/login" className="text-primary underline underline-offset-4" data-testid="register-to-login-link">
                  Sign in
                </Link>
              </div>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
